import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Hook to manage subtopic navigation within a subject module.
 * @param {Array} subtopics - Array of subtopic objects with { name, path } structure
 * @param {string} currentPath - Current page path (e.g., '/daa-m1-s1')
 * @param {string} backToSubjectPath - Path to return to subject module page
 * @returns {Object} Navigation state: { prevPath, nextPath, isFirst, isLast, currentIndex }
 */
export const useSubtopicNavigation = (subtopics, currentPath, backToSubjectPath) => {
    const navigate = useNavigate();

    const navigationState = useMemo(() => {
        if (!subtopics || subtopics.length === 0) {
            return {
                prevPath: null,
                nextPath: null,
                isFirst: true,
                isLast: true,
                currentIndex: -1,
            };
        }

        // Normalize paths and find current subtopic index (tolerant matching)
        const normalize = (p) => (p || '').split('?')[0].replace(/\/+$|^\s+|\s+$/g, '');
        const cur = normalize(currentPath);

        const currentIndex = subtopics.findIndex((sub) => {
            const subPath = normalize(sub.path);
            return (
                subPath === cur ||
                cur.endsWith(subPath) ||
                subPath.endsWith(cur)
            );
        });

        if (currentIndex === -1) {
            // Path not found in subtopics array
            return {
                prevPath: null,
                nextPath: null,
                isFirst: true,
                isLast: true,
                currentIndex: -1,
            };
        }

        const isFirst = currentIndex === 0;
        const isLast = currentIndex === subtopics.length - 1;

        return {
            prevPath: isFirst ? null : subtopics[currentIndex - 1].path,
            nextPath: isLast ? null : subtopics[currentIndex + 1].path,
            isFirst,
            isLast,
            currentIndex,
        };
    }, [subtopics, currentPath]);

    const handleBack = () => {
        if (!navigationState.isFirst && navigationState.prevPath) {
            navigate(navigationState.prevPath);
        } else if (navigationState.isFirst && backToSubjectPath) {
            navigate(backToSubjectPath);
        }
    };

    return {
        ...navigationState,
        handleBack,
        backToSubjectPath,
    };
};
